'use client'

import React, { useCallback, useEffect, useState } from 'react'
import useEmblaCarousel from 'embla-carousel-react'
import type { EmblaOptionsType } from 'embla-carousel'
import { cn } from '@/utilities/ui'
import RichText from '@/components/RichText'
import { Media } from '../../components/Media'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import './embla.css'

import type { EmblaSlider as EmblaSliderProps } from '@/payload-types'

type Slide = {
  media: any
  caption?: any
  altText?: string
}

type Props = EmblaSliderProps & {
  className?: string
  imgClassName?: string
  captionClassName?: string
}

export const EmblaSlider: React.FC<Props> = (props) => {
  const {
    title,
    slides,
    showNavigation = true,
    showDots = true,
    autoplay = false,
    autoplaySpeed = 5000,
    enableGutter = true,
    className,
    imgClassName,
    captionClassName,
  } = props

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: 'start',
  } as EmblaOptionsType)

  const [selectedIndex, setSelectedIndex] = useState(0)
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([])

  const onSelect = useCallback(() => {
    if (!emblaApi) return
    setSelectedIndex(emblaApi.selectedScrollSnap())
  }, [emblaApi])

  const scrollTo = useCallback((index: number) => emblaApi && emblaApi.scrollTo(index), [emblaApi])

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi])

  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi])

  useEffect(() => {
    if (!emblaApi) return

    onSelect()
    setScrollSnaps(emblaApi.scrollSnapList())
    emblaApi.on('select', onSelect)
    emblaApi.on('reInit', onSelect)

    return () => {
      emblaApi.off('select', onSelect)
      emblaApi.off('reInit', onSelect)
    }
  }, [emblaApi, onSelect])

  useEffect(() => {
    if (!autoplay || !emblaApi || !autoplaySpeed) return

    const interval = setInterval(() => {
      emblaApi.scrollNext()
    }, autoplaySpeed)

    return () => clearInterval(interval)
  }, [autoplay, autoplaySpeed, emblaApi])

  if (!slides || slides.length === 0) return null

  return (
    <div
      className={cn(
        'relative',
        {
          container: enableGutter,
        },
        className,
      )}
    >
      {title && (
        <div className="mb-8">
          <h2 className="text-2xl font-bold">{title}</h2>
        </div>
      )}

      <div className="relative overflow-hidden rounded-[0.8rem]">
        <div className="embla" ref={emblaRef}>
          <div className="embla__container flex">
            {slides.map((slide, index: number) => (
              <div key={index} className="embla__slide min-w-0 flex-[0_0_100%]">
                <div className="relative">
                  <Media
                    imgClassName={cn(
                      'w-full h-auto border border-border rounded-[0.8rem]',
                      imgClassName,
                    )}
                    resource={slide.media}
                    alt={slide.altText || ''}
                  />
                  {slide.caption && (
                    <div
                      className={cn(
                        'absolute bottom-0 left-0 right-0 bg-black/50 text-white p-4 rounded-b-[0.8rem]',
                        captionClassName,
                      )}
                    >
                      <RichText data={slide.caption} enableGutter={false} />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {showNavigation && slides.length > 1 && (
          <>
            <button
              className="absolute left-4 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/80 p-2 text-gray-800 shadow-lg transition-all duration-200 hover:bg-white"
              onClick={scrollPrev}
              aria-label="Previous slide"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              className="absolute right-4 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/80 p-2 text-gray-800 shadow-lg transition-all duration-200 hover:bg-white"
              onClick={scrollNext}
              aria-label="Next slide"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </>
        )}

        {showDots && slides.length > 1 && (
          <div className="absolute bottom-4 left-1/2 z-10 flex -translate-x-1/2 gap-2">
            {scrollSnaps.map((_, index) => (
              <button
                key={index}
                className={cn('w-3 h-3 rounded-full transition-all duration-200', {
                  'bg-white': index === selectedIndex,
                  'bg-white/50 hover:bg-white/75': index !== selectedIndex,
                })}
                onClick={() => scrollTo(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
